import type { Server } from '../../types'

/**
 * Whether a "home voice channel" id still resolves to a channel the user can
 * actually reach.
 *
 * The home channel is persisted on `user.settings.homeVoiceChannelId`, but
 * deleting a channel (DELETE /channels/:id) never clears that setting, so it
 * can dangle to a channel that no longer exists. `GET /servers` returns every
 * server the user is a member of with soft-deleted (and invisible) channels
 * already filtered out (`deletedAt: null` + visibility strip), so once the
 * server list is loaded an id that isn't found in ANY server means the channel
 * was deleted or the user lost access — navigating there lands "nowhere".
 *
 * Used to hide the "go to home channel" house button (and bail its click)
 * instead of letting it route to a dead channel.
 */
export function homeChannelExists(
  servers: Server[],
  homeChannelId: string | null | undefined,
): boolean {
  if (!homeChannelId) return false
  return servers.some(
    (s) =>
      (s.channels ?? []).some((c) => c.id === homeChannelId) ||
      (s.categories ?? []).some((cat) =>
        (cat.channels ?? []).some((c) => c.id === homeChannelId),
      ),
  )
}
